import * as React from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./MobileInput.scss";
import indexer from "../../types/genericTypes";
import { required } from "../../utils/validation";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { trackFormFieldEntered } from "../../Services/formFieldTracker";
import { useClientErrorTracking } from "../../Services/clientErrorTracker";

export interface IMobileInputProps {
  disabled?: boolean;
  defaultPhoneNumber?: string;      // Only phone number without dial code (e.g., "9876543210")
  defaultDialCode?: string;         // Only dial code (e.g., "+91" or "91")
  placeholder?: string;
  value?: string;
  prefix?: React.ReactChild;
  suffix?: React.ReactChild;
  helpText?: string;
  helpTextColor?: "grey" | "blue";
  errorText?: string | null;
  required?: boolean;
  className?: string;
  maxLength?: number;
  id?: string;
  name?: string;
  autoComplete?: React.HTMLInputAutoCompleteAttribute;
  pattern?: RegExp;
  
  // Controlled values (separate)
  phoneNumber?: string;             // Controlled phone number
  dialCode?: string;                // Controlled dial code
  
  // Callbacks - returns separate values
  onChange?: (dialCode: string, phoneNumber: string, countryShortCode: string, countryName:string) => void;
  onDialCodeChange?: (dialCode: string, countryCode: string, countryName: string) => void;
  onPhoneNumberChange?: (phoneNumber: string) => void;
  onBlur?: (error: indexer, event?: any) => void;

  // Phone specific props
  defaultCountry?: string;
  onlyCountries?: string[];
  preferredCountries?: string[];
  excludeCountries?: string[];
  enableSearch?: boolean;
  disableDropdown?: boolean;
  disableCountryCode?: boolean;
  countryCodeEditable?: boolean;
  enableAreaCodes?: boolean;
  enableLongNumbers?: boolean;
  autoFormat?: boolean;
  masks?: { [key: string]: string };
  /** Accessible name when no external <label htmlFor> is present */
  ariaLabel?: string;
  /** External label id for aria-labelledby */
  ariaLabelledBy?: string;
}

interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
  format: string;
}

// Dial code mapping for common countries
const DIAL_CODES: { [key: string]: string } = {
  in: "91",
  us: "1",
  gb: "44",
  ca: "1",
  au: "61",
  ae: "971",
  sg: "65",
  de: "49",
  fr: "33",
  jp: "81",
};

const MobileInput: React.FC<IMobileInputProps> = (props) => {
  // Track dial code and phone number separately
  const [currentDialCode, setCurrentDialCode] = React.useState<string>(() => {
    if (props.defaultDialCode) {
      return props.defaultDialCode.replace("+", "");
    }
    return DIAL_CODES[props.defaultCountry || "in"] || "91";
  });

  const [currentPhoneNumber, setCurrentPhoneNumber] = React.useState<string>(
    props.defaultPhoneNumber || ""
  );

  const [errText, setErrText] = React.useState(props.errorText);
  const [focusClass, setFocusClass] = React.useState("");
  
  // Track previous dial code to detect country changes
  const prevDialCodeRef = React.useRef(currentDialCode);

  // Combine dial code and phone number for the PhoneInput value
  const combinedValue = React.useMemo(() => {
    return `${currentDialCode}${currentPhoneNumber}`;
  }, [currentDialCode, currentPhoneNumber]);

  // Handle controlled phoneNumber prop
  React.useEffect(() => {
    if (props.phoneNumber !== undefined) {
      setCurrentPhoneNumber(props.phoneNumber);
    }
  }, [props.phoneNumber]);

  // Handle controlled dialCode prop
  React.useEffect(() => {
    if (props.dialCode !== undefined) {
      setCurrentDialCode(props.dialCode.replace("+", ""));
    }
  }, [props.dialCode]);

  // Handle default values changes
  React.useEffect(() => {
    if (props.defaultPhoneNumber !== undefined) {
      setCurrentPhoneNumber(props.defaultPhoneNumber);
    }
  }, [props.defaultPhoneNumber]);

  React.useEffect(() => {
    if (props.defaultDialCode !== undefined) {
      setCurrentDialCode(props.defaultDialCode.replace("+", ""));
    }
  }, [props.defaultDialCode]);

  React.useEffect(() => {
    setErrText(props.errorText);
  }, [props.errorText]);
  useClientErrorTracking(props.errorText, props.name || props.id || "mobile_number", props.ariaLabel);

  function isRequired(value: any): boolean {
    return !props.required || (props.required && !!required(value));
  }

  const validatePhoneNumber = (phoneNumber: string, dialCode: string, country: any) => {
    if (!phoneNumber) return false;

    const fullNumber = `+${dialCode}${phoneNumber}`;
    const parsedNumber = parsePhoneNumberFromString(
      fullNumber,
      country?.countryCode?.toUpperCase()
    );

    if (!parsedNumber) return false;
    if (!parsedNumber.isPossible()) return false;
    if (!parsedNumber.isValid()) return false;

    return true;
  };

  function handleBlur(
    e: React.ChangeEvent<HTMLInputElement>,
    code: CountryData | {}
  ) {
    setFocusClass("");

    let errorObj: indexer = {};
    errorObj = {
      ...errorObj,
      pattern: validatePhoneNumber(currentPhoneNumber, currentDialCode, code),
    };

    if (props.required) {
      errorObj = { ...errorObj, required: isRequired(currentPhoneNumber) };
    }

    props.onBlur && props.onBlur(errorObj, e);
  }

  function handleChange(
    phoneValue: string,
    country: CountryData,
    event: React.ChangeEvent<HTMLInputElement>,
    formattedValue: string
  ) {
    trackFormFieldEntered({
      element: (event?.target as HTMLInputElement) || null,
      fieldName: props.name || props.id || "mobile_number",
      fieldLabel: props.ariaLabel,
      fieldType: "tel",
      hasValue: !!phoneValue,
    });

    const newDialCode = country?.dialCode || "";
    const newDialCodeLength = newDialCode.length;
    const newPhoneNumber = phoneValue.slice(newDialCodeLength);
    
    // Check if country/dial code changed
    const dialCodeChanged = prevDialCodeRef.current !== newDialCode;
    
    // Validation: Block numbers starting with 1-5
    const firstDigit = newPhoneNumber?.[0];
    if (firstDigit && ["1", "2", "3", "4", "5"].includes(firstDigit)) {
      return;
    }

    // Max length validation
    if (props.maxLength && newPhoneNumber.length > props.maxLength) {
      return;
    }

    setErrText("");

    // Update dial code if changed
    if (dialCodeChanged) {
      prevDialCodeRef.current = newDialCode;
      setCurrentDialCode(newDialCode);

      // Callback for dial code change
      props.onDialCodeChange?.(
        `+${newDialCode}`,
        country.countryCode,
        country.name
      );
    }

    // Update phone number
    setCurrentPhoneNumber(newPhoneNumber);

    // Callback for phone number change
    props.onPhoneNumberChange?.(newPhoneNumber);

    // Combined callback
    props.onChange?.(`+${newDialCode}`, newPhoneNumber, country.countryCode.toUpperCase(), country.name);
  }

  function handleFocus() {
    setFocusClass(" focus");
  }

  const inputId = props.id || props.name;
  const errorId = inputId ? `${inputId}-error` : undefined;
  const helpId = inputId ? `${inputId}-help` : undefined;

  return (
    <div className={"input_field_wrapper " + (props.className || "")}>
      <div
        className={
          "input_field_cont" +
          focusClass +
          (props.errorText ? " error" : "") +
          " phone_input_container"
        }
      >
        {props.prefix && <div className="input_prefix">{props.prefix}</div>}

        <PhoneInput
          country={props.defaultCountry || "in"}
          value={combinedValue}
          onChange={handleChange}
          onBlur={(event, code) => handleBlur(event, code)}
          onFocus={handleFocus}
          placeholder={props.placeholder || "Enter phone number"}
          inputProps={{
            id: inputId,
            name: props.name,
            autoComplete: props.autoComplete ?? "tel",
            disabled: props.disabled,
            required: props.required || undefined,
            "aria-required": props.required || undefined,
            "aria-invalid": props.errorText ? true : undefined,
            "aria-describedby":
              [
                props.errorText && errorId,
                props.helpText && helpId,
              ]
                .filter(Boolean)
                .join(" ") || undefined,
            "aria-label": props.ariaLabelledBy
              ? undefined
              : props.ariaLabel ||
                (!inputId ? props.placeholder || "Phone number" : undefined),
            "aria-labelledby": props.ariaLabelledBy,
          }}
          onlyCountries={props.onlyCountries}
          preferredCountries={props.preferredCountries || ["in", "us", "gb"]}
          enableSearch={props.enableSearch ?? true}
          disableDropdown={props.disableDropdown}
          disableCountryCode={props.disableCountryCode}
          countryCodeEditable={false}
          enableLongNumbers={props.enableLongNumbers}
          autoFormat={props.autoFormat ?? false}
        />

        {props.suffix && <div className="input_suffix">{props.suffix}</div>}
      </div>

      {props.errorText && (
        <p className="help_text red" id={errorId} role="alert" aria-live="assertive">
          {props.errorText}
        </p>
      )}

      {props.helpText && (
        <p className={`help_text ${props.helpTextColor || "blue"}`} id={helpId}>
          {props.helpText}
        </p>
      )}
    </div>
  );
};

export default MobileInput;